import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid';
import { LabLostAndFoundMissingReason, LabLostAndFoundParams } from 'api/labs';
import { hasActiveReadOnlyGridFilters } from 'components/atoms/BaseDataGrid/ReadOnlyDataGrid';
import { filter, join, map } from 'lodash';
import { ArrayParam, JsonParam, NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { toDate } from 'utils/dateTime';

import { SOURCE_STUDY_GRID_FIELD } from './fields';

const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const LOST_AND_FOUND_MISSING_REASONS: LabLostAndFoundMissingReason[] = [
  'missingIngest',
  'missingCaseAssignment',
];
export const missingReasonLabels: Record<LabLostAndFoundMissingReason, string> = {
  missingIngest: 'Missing ingest',
  missingCaseAssignment: 'Missing case assignment',
};

const queryParamConfig = {
  pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
  sourceStudyIds: withDefault(ArrayParam, []),
  missingReasons: withDefault(ArrayParam, []),
  uploadedAtStart: StringParam,
  uploadedAtEnd: StringParam,
  searchQuery: StringParam,
  // Written by the organization return URL to restore the page and target study the user left.
  page: NumberParam,
  filters: JsonParam,
};

const normalizeStringArray = (values: (string | null)[] | null | undefined) =>
  filter(map(values || [], (value) => value || undefined)) as string[];

const getQuickFilterQuery = (filterModel: GridFilterModel) =>
  join(
    map(filterModel.quickFilterValues || [], (value) => String(value ?? '').trim()),
    ' '
  ).trim();

const getFilterItem = (filterModel: GridFilterModel, field: string) =>
  filterModel.items.find((item) => item.field === field);

const normalizeFilterValues = (item?: GridFilterItem) => {
  if (Array.isArray(item?.value)) {
    return normalizeStringArray(item.value);
  }

  return item?.value ? [String(item.value)] : [];
};

const getDateRange = (item?: GridFilterItem) => {
  const [startValue, endValue] = Array.isArray(item?.value) ? item.value : [item?.value, item?.value];
  const startDate = toDate(startValue);
  const endDate = toDate(endValue);

  switch (item?.operator) {
    case 'after':
    case 'onOrAfter':
      return { uploadedAtStart: startDate?.toISOString(), uploadedAtEnd: undefined };
    case 'before':
    case 'onOrBefore':
      return { uploadedAtStart: undefined, uploadedAtEnd: endDate?.toISOString() };
    case 'between':
      return { uploadedAtStart: startDate?.toISOString(), uploadedAtEnd: endDate?.toISOString() };
    default:
      return { uploadedAtStart: undefined, uploadedAtEnd: undefined };
  }
};

export const useLostAndFoundQueryParams = () => {
  const [queryParams, setQueryParams] = useQueryParams(queryParamConfig);
  const sourceStudyIds = normalizeStringArray(queryParams.sourceStudyIds);
  const missingReasons = normalizeStringArray(queryParams.missingReasons).filter(
    (value): value is LabLostAndFoundMissingReason => value in missingReasonLabels
  );
  const requestParams: LabLostAndFoundParams = {
    sourceStudyIds: sourceStudyIds.length ? sourceStudyIds : undefined,
    uploadedAtStart: queryParams.uploadedAtStart || undefined,
    uploadedAtEnd: queryParams.uploadedAtEnd || undefined,
    searchQuery: queryParams.searchQuery?.trim() || undefined,
  };

  const filterItems: GridFilterItem[] = [];
  if (sourceStudyIds.length) {
    filterItems.push({
      id: 'sourceStudyIds',
      field: SOURCE_STUDY_GRID_FIELD,
      operator: 'isAnyOf',
      value: sourceStudyIds,
    });
  }
  if (missingReasons.length) {
    filterItems.push({ id: 'missingReasons', field: 'missingReasons', operator: 'isAnyOf', value: missingReasons });
  }
  if (queryParams.uploadedAtStart || queryParams.uploadedAtEnd) {
    const startDate = toDate(queryParams.uploadedAtStart);
    const endDate = toDate(queryParams.uploadedAtEnd);
    filterItems.push({
      id: 'uploadedAt',
      field: 'uploadedAt',
      operator: startDate && endDate ? 'between' : startDate ? 'onOrAfter' : 'onOrBefore',
      value: startDate && endDate ? [startDate, endDate] : startDate || endDate,
    });
  }
  const filterModel: GridFilterModel = {
    items: filterItems,
    quickFilterValues: queryParams.searchQuery ? [queryParams.searchQuery] : [],
  };
  const handleFilterModelChange = (nextFilterModel: GridFilterModel) => {
    const dateRange = getDateRange(getFilterItem(nextFilterModel, 'uploadedAt'));
    setQueryParams(
      {
        sourceStudyIds: normalizeFilterValues(getFilterItem(nextFilterModel, SOURCE_STUDY_GRID_FIELD)) || undefined,
        missingReasons: normalizeFilterValues(getFilterItem(nextFilterModel, 'missingReasons')) || undefined,
        uploadedAtStart: dateRange.uploadedAtStart,
        uploadedAtEnd: dateRange.uploadedAtEnd,
        searchQuery: getQuickFilterQuery(nextFilterModel) || undefined,
      },
      'replaceIn'
    );
  };

  const handlePageSizeChange = (pageSize: number) => setQueryParams({ pageSize }, 'replaceIn');
  const returnTargetStudyId = typeof queryParams.filters?.studyId === 'string' ? queryParams.filters.studyId : '';
  const returnPage =
    typeof queryParams.page === 'number' && Number.isSafeInteger(queryParams.page) && queryParams.page > 0
      ? queryParams.page - 1
      : undefined;

  return {
    returnPage,
    returnTargetStudyId,
    filterModel,
    handleFilterModelChange,
    handlePageSizeChange,
    hasActiveFilters: hasActiveReadOnlyGridFilters(filterModel),
    missingReasons: missingReasons.length ? missingReasons : LOST_AND_FOUND_MISSING_REASONS,
    pageSize: PAGE_SIZE_OPTIONS.includes(queryParams.pageSize) ? queryParams.pageSize : DEFAULT_PAGE_SIZE,
    requestParams,
    setMissingReasons: (nextMissingReasons: LabLostAndFoundMissingReason[]) =>
      setQueryParams(
        {
          missingReasons:
            nextMissingReasons.length === LOST_AND_FOUND_MISSING_REASONS.length ? undefined : nextMissingReasons,
        },
        'replaceIn'
      ),
  };
};
